import { RectangleTool } from "./RectangleTool";
import {
  createRectangleFrom3Points,
  projectPointOntoLine,
  normalizeRectAlongAxis,
  normalizeRectWinding,
} from "../utils/geometry";
import proj4 from "proj4";
import { getUtmProj } from "../utils/projection";

export class MastRectangleTool extends RectangleTool {
  constructor(map, setGeometries, setWorkflowStep, getSelectedLayout, pointTool, arrowTool) {
    super(map, setGeometries, setWorkflowStep);
    this.getSelectedLayout = getSelectedLayout;
    this.pointTool = pointTool;
    this.arrowTool = arrowTool;

    this.fixedB_utm = null;
    this.referenceVector = null;
    this.referenceUTM = null;
    // Laengsachse des Spielfelds (UTM-Vektor) — entscheidet, welche Kante
    // des Mastrechtecks die Nummer 0 traegt (siehe _normalize unten).
    this.fieldLongAxis = null;
  }

  setReferenceFromFieldRectangle(fieldRectangle) {
    if (!fieldRectangle || fieldRectangle.length < 2) return; 
    const [A_ll, B_ll] = fieldRectangle;
    const wgs84 = "EPSG:4326";
    const utm = getUtmProj(A_ll); 
    const A_utm = proj4(wgs84, utm, A_ll);
    const B_utm = proj4(wgs84, utm, B_ll);  
    this.referenceVector = [
      B_utm[0] - A_utm[0],
      B_utm[1] - A_utm[1],
    ];
    this.referenceUTM = utm;

    // Feld-Laengsachse merken: die laengere der beiden ersten Feldkanten
    // (das Feldrechteck wird nie umsortiert, seine erste Kante kann kurz
    // oder lang sein). Mit nur zwei Ecken bleibt es bei der ersten Kante.
    let longAxis = this.referenceVector;
    if (fieldRectangle.length >= 3) {
      const C_utm = proj4(wgs84, utm, fieldRectangle[2]);
      const v2 = [C_utm[0] - B_utm[0], C_utm[1] - B_utm[1]];
      if (Math.hypot(v2[0], v2[1]) > Math.hypot(longAxis[0], longAxis[1])) {
        longAxis = v2;
      }
    }
    this.fieldLongAxis = longAxis;
  }

  // Gemeinsame Normalisierung fuer Geister-Vorschau und Platzierung:
  // ZUERST den Umlaufsinn festziehen (im Uhrzeigersinn), DANN die Kante
  // parallel zur FELD-Laengsachse nach vorn sortieren. Reihenfolge
  // wichtig: die Umkehr tauscht, welche Kante die Nummer 0 traegt.
  //
  // Bis 21.09.2026 stand hier normalizeRectLongFirst — die laengere Kante
  // des MASTRECHTECKS galt als Langseite. Das kippte, sobald ein Nutzer
  // das Rechteck kuerzer als breit zog (eingerueckte Masten bei 0·2·2·0,
  // Eckmasten weit innerhalb der Feldenden bei 2·1·2 / 1·2·1): dann lagen
  // alle Masten auf den Stirnseiten oder mitten im Feld (Sandbox-Tests
  // 21.09., Projekte 399/401/409/410/415). Die Presets meinen mit Kante
  // 0/2 immer die Langseiten des FELDES — also entscheidet dessen Achse.
  _normalize(rectUtm) {
    return normalizeRectAlongAxis(normalizeRectWinding(rectUtm), this.fieldLongAxis);
  }

  // Schiene fuer die erste Kante: parallel zur Feldkante — aber zu DERJENIGEN
  // der beiden Feldachsen (laengs oder quer), der die Mausrichtung naeher
  // kommt. Bisher gab es nur EINE Richtung: die Kante, die der Nutzer beim
  // Spielfeld zufaellig ZUERST gezeichnet hatte (das Feldrechteck wird nie
  // umsortiert). Wer das Feld mit der kurzen Seite begonnen hatte, konnte
  // das Mastrechteck nur quer beginnen — Philips "komisch beschnittene
  // Freiheitsgrade" (25.08.2026). Fuer die Mastplatzierung ist die Wahl der
  // Startkante egal: _normalize richtet Kante 0 hinterher ohnehin an der
  // Feld-Laengsachse aus.
  _railVector(A_utm, mouse_utm) {
    const v1 = this.referenceVector;
    const v2 = [-v1[1], v1[0]];
    const p1 = projectPointOntoLine(A_utm, v1, mouse_utm);
    const p2 = projectPointOntoLine(A_utm, v2, mouse_utm);
    const d1 = Math.hypot(mouse_utm[0] - p1[0], mouse_utm[1] - p1[1]);
    const d2 = Math.hypot(mouse_utm[0] - p2[0], mouse_utm[1] - p2[1]);
    return d1 <= d2 ? v1 : v2;
  }

  onClick(lngLat) {
    this.points.push([lngLat.lng, lngLat.lat]);

    if (
      this.points.length === 2 &&
      this.referenceVector &&
      this.referenceUTM
    ) {
      const wgs84 = "EPSG:4326";
      const A_utm = proj4(wgs84, this.referenceUTM, this.points[0]);
      const B_click_utm = proj4(wgs84, this.referenceUTM, this.points[1]);

      this.fixedB_utm = projectPointOntoLine(
        A_utm,
        this._railVector(A_utm, B_click_utm),
        B_click_utm
      );
      this.points[1] = proj4(this.referenceUTM, wgs84, this.fixedB_utm);
    }

    if (this.points.length === 3) {
      return this.finalizeRectangle();
    }
  }

  onMouseMove(lngLat) {
    if (this.points.length < 1) return;

    const wgs84 = "EPSG:4326";
    const A = this.points[0];
    const utm = this.referenceUTM || getUtmProj(A);

    const A_utm = proj4(wgs84, utm, A);
    const mouse_utm = proj4(wgs84, utm, [lngLat.lng, lngLat.lat]);

    let B_utm;

    if (this.points.length === 1 && this.referenceVector) {
      B_utm = projectPointOntoLine(A_utm, this._railVector(A_utm, mouse_utm), mouse_utm);
    } else if (this.points.length >= 2 && this.fixedB_utm) {
      B_utm = this.fixedB_utm;
    } else {
      B_utm = mouse_utm;
    }

    const B_ll = proj4(utm, wgs84, B_utm);
    this.updatePreviewLine([A, B_ll]);

    // ── Tape measure for mast rectangle ─────────────────────────────
    if (this.points.length === 1) {
      // Show length along the field edge direction
      const lengthM = Math.hypot(B_utm[0] - A_utm[0], B_utm[1] - A_utm[1]);
      this.showMeasurement(lngLat, `${lengthM.toFixed(1)} m`);
    }

    if (this.points.length === 2) {
      const B = this.points[1];
      const B_utm_final = proj4(wgs84, utm, B);
      const rect = createRectangleFrom3Points(A_utm, B_utm_final, mouse_utm)
          .map((p) => proj4(utm, wgs84, p));
      if (!rect) return;
      this.updatePreviewRectangle(rect);
      // Geister-Masten live mitziehen (Philips Wunsch 26.08.2026): bei den
      // komplexeren Presets sieht man sonst erst NACH dem 3. Klick, wo die
      // Masten landen.
      this._updateGhostMasts(rect);

      // Show length × width (width = offset from field edge where masts sit)
      const lengthM = Math.hypot(B_utm_final[0] - A_utm[0], B_utm_final[1] - A_utm[1]);
      const AB_dx = B_utm_final[0] - A_utm[0];
      const AB_dy = B_utm_final[1] - A_utm[1];
      const AB_len = Math.hypot(AB_dx, AB_dy);
      const nx = -AB_dy / AB_len;
      const ny = AB_dx / AB_len;
      const widthM = Math.abs(
        (mouse_utm[0] - A_utm[0]) * nx + (mouse_utm[1] - A_utm[1]) * ny
      );
      this.showMeasurement(lngLat, `${lengthM.toFixed(1)} × ${widthM.toFixed(1)} m`);
    }
  }

  // ── Geister-Vorschau der Mastpositionen ─────────────────────────
  // Rechnet fuer das aktuell aufgezogene Rechteck aus, wo die Masten des
  // gewaehlten Presets landen wuerden, und zeigt sie als halbtransparente
  // Punkte. WICHTIG: exakt dieselbe Normalisierung (Umlaufsinn, dann lange
  // Kante nach vorn) wie finalizeRectangle — sonst zeigt die Vorschau
  // andere Kanten als die endgueltige Platzierung.
  _updateGhostMasts(rectLL) {
    const layout = this.getSelectedLayout?.();
    if (!layout) return;

    const wgs84 = "EPSG:4326";
    const utmProj = getUtmProj(rectLL[0]);
    const rectUtm = rectLL.map((p) => proj4(wgs84, utmProj, p));
    const normRect = this._normalize(rectUtm)
      .map((p) => proj4(utmProj, wgs84, p));

    // Gleiche Interpolation wie PointTool.placePoints (edge + t)
    const punkte = layout.edges.map((edge, i) => {
      const start = normRect[edge];
      const end = normRect[(edge + 1) % 4];
      return [
        start[0] + layout.t[i] * (end[0] - start[0]),
        start[1] + layout.t[i] * (end[1] - start[1]),
      ];
    });

    const data = {
      type: "FeatureCollection",
      features: punkte.map((c) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: c },
      })),
    };
    if (this.map.getSource("preview-ghost-masts")) {
      this.map.getSource("preview-ghost-masts").setData(data);
    } else {
      this.map.addSource("preview-ghost-masts", { type: "geojson", data });
      this.map.addLayer({
        id: "preview-ghost-masts",
        type: "circle",
        source: "preview-ghost-masts",
        paint: {
          "circle-radius": 6,
          "circle-color": "#f97316",
          "circle-opacity": 0.55,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
    }
  }

  // Aufraeumen laeuft ueber die Basisklasse mit: finalizeRectangle ruft
  // clearPreviews, Undo ruft clearPreviewGraphics — beide hier erweitert,
  // damit die Geister-Masten nicht liegen bleiben.
  clearPreviews() {
    if (this.map.getSource("preview-ghost-masts")) {
      this.map.removeLayer("preview-ghost-masts");
      this.map.removeSource("preview-ghost-masts");
    }
    super.clearPreviews();
  }

  clearPreviewGraphics() {
    if (this.map.getSource("preview-ghost-masts")) {
      this.map.getSource("preview-ghost-masts")
        .setData({ type: "FeatureCollection", features: [] });
    }
    super.clearPreviewGraphics();
  }

  finalizeRectangle() {
    const rect = super.finalizeRectangle();
    if (!rect) return;

    const layout = this.getSelectedLayout?.();
    if (!layout) return;

    // Mast layouts hard-code edges ∈ {0, 2} (see config/mastlayouts.js),
    // which means the LONG SIDES of the FIELD. The user's draw order and
    // the rectangle's aspect ratio must not decide which edge gets the
    // masts — see _normalize (winding, then field-axis alignment).
    //
    // Project to UTM before any length/angle comparison: lng/lat units
    // are biased (1° lat = 111 km vs 1° lng ≈ 68 km at 52° N), UTM gives
    // true metres.
    const wgs84 = "EPSG:4326";
    const utmProj = getUtmProj(rect[0]);
    const rectUtm = rect.map((p) => proj4(wgs84, utmProj, p));
    const normRectUtm = this._normalize(rectUtm);
    const normRect = normRectUtm.map((p) => proj4(utmProj, wgs84, p));

    const layoutPoints = layout.edges.map((edge, i) => ({
      edge,
      t: layout.t[i],
      angle: layout.angles[i],
    }));

    this.pointTool.placePoints(normRect, layoutPoints);
    this.setGeometries(function updater(prev) {
      // Store the normalised rect (corners rotated, geometry identical).
      // This keeps state internally consistent: any later code that
      // re-reads geometries.mastsRectangle gets the same orientation
      // the masts were placed against.
      return { ...prev, mastsRectangle: normRect };
    });
    this.setWorkflowStep((prev) => prev + 1);

    return normRect;
  }
}